import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import type {
  AiAnalysisResult,
  AiKind,
  AiMissingField,
  AiSuggestedPosition,
  MaPositionInput,
} from "@/lib/material";
import { filterMaterialUnits, normalizeMaterialUnit } from "@/lib/materialUnits";
import type { AuftragHintStatus } from "@/pages/material/MaDialog";

export interface AiSuggestionsPanelProps {
  /** Wenn true, ist eine KI-Analyse gerade unterwegs (zeigt Spinner & Hinweis). */
  loading: boolean;
  /** Ergebnis der zuletzt durchgeführten Analyse. */
  result: AiAnalysisResult | null;
  /** Datei-Name, der gerade analysiert wurde. Wird nur als Hinweistext gezeigt. */
  fileName?: string | null;
  /** Fehlertext, wenn die Analyse fehlgeschlagen ist. */
  error?: string | null;
  /** Status der automatischen Auftragsverknüpfung (Suche/Treffer/Konflikt). */
  auftragHintStatus?: AuftragHintStatus;
  /** Übernimmt die ausgewählten Positionen in den MA-Dialog. */
  onApply: (positions: MaPositionInput[]) => void;
  /** Schließt das Panel (bzw. verwirft die Vorschläge). */
  onDismiss: () => void;
}

const KIND_LABELS: Record<AiKind, string> = {
  skizze: "Skizze erkannt",
  handschrift: "Handschriftliche Liste erkannt",
  ausgedruckt: "Ausgedruckte Liste erkannt",
  foto: "Foto erkannt",
  kein_material: "Kein Material erkannt",
};

const KIND_BADGE: Record<AiKind, string> = {
  skizze: "bg-accent-soft text-accent",
  handschrift: "bg-accent-soft text-accent",
  ausgedruckt: "bg-accent-soft text-accent",
  foto: "bg-fill-tertiary text-label-secondary",
  kein_material: "bg-fill-tertiary text-label-secondary",
};

const CATEGORY_HINT: Record<string, string> = {
  blech: "Blech",
  schraube: "Schraube",
  mutter: "Mutter",
  profil: "Profil",
  rohr: "Rohr",
  dichtung: "Dichtung",
  kabel: "Kabel",
  elektro: "Elektro",
  werkzeug: "Werkzeug",
  sonstiges: "Sonstiges",
};

interface RowState {
  selected: boolean;
  /** menge als String, damit Eingabe natürlich bleibt. */
  menge: string;
  einheit: string;
  text: string;
  notes: string;
  /** Antworten zu den ``missing``-Feldern (per field-key). */
  answers: Record<string, string>;
}

function defaultRow(p: AiSuggestedPosition): RowState {
  return {
    selected: true,
    menge: p.menge != null ? String(p.menge) : "",
    einheit: p.einheit ?? "",
    text: p.text,
    notes: p.notes ?? "",
    answers: {},
  };
}

export function AiSuggestionsPanel({
  loading,
  result,
  fileName,
  error,
  auftragHintStatus,
  onApply,
  onDismiss,
}: AiSuggestionsPanelProps) {
  // Stabile Position-Liste – verhindert, dass `positions` bei jedem Render
  // eine neue Identität bekommt (sonst flackert der useMemo-/useEffect-Trigger).
  const positions = useMemo<AiSuggestedPosition[]>(
    () => result?.positions ?? [],
    [result],
  );
  const [rows, setRows] = useState<RowState[]>(() =>
    positions.map(defaultRow),
  );

  // Bei neuer Analyse Reihen neu initialisieren. Wir vergleichen über einen
  // stabilen Key der erkannten Positionen, damit Eingaben in den Reihen nicht
  // bei jeder unbedeutenden Re-Renderschleife gelöscht werden.
  const positionsKey = useMemo(
    () => positions.map((p) => `${p.text}|${p.menge}|${p.einheit}`).join("§"),
    [positions],
  );
  useEffect(() => {
    setRows(positions.map(defaultRow));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionsKey]);

  if (loading) {
    return (
      <div className="rounded-ios border border-separator bg-card-elevated p-4">
        <div className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner />
          <span>
            KI analysiert das Bild
            {fileName ? ` „${fileName}“` : ""} – das dauert ein paar Sekunden …
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error" title="KI-Analyse fehlgeschlagen">
        <div className="flex flex-col gap-2">
          <span>{error}</span>
          <div>
            <Button variant="secondary" size="sm" onClick={onDismiss}>
              Schließen
            </Button>
          </div>
        </div>
      </Alert>
    );
  }

  if (!result) return null;

  const kindLabel = KIND_LABELS[result.kind] || "Analyse-Ergebnis";
  const confidencePct = Math.round((result.confidence || 0) * 100);

  if (result.kind === "kein_material" || positions.length === 0) {
    return (
      <div className="space-y-2">
        <AuftragHintBadge
          hint={result.auftrag_hint}
          status={auftragHintStatus}
        />
        <Alert variant="info" title={kindLabel}>
          <div className="flex flex-col gap-2 text-sm">
            <span>
              Ich konnte aus dem Bild
              {fileName ? ` „${fileName}“` : ""} keine Materialliste oder
              Skizze ableiten.
              {result.global_summary
                ? ` Stattdessen sehe ich: „${result.global_summary}“.`
                : ""}{" "}
              Das Bild bleibt natürlich als Anhang an der Anforderung.
            </span>
            <div>
              <Button variant="secondary" size="sm" onClick={onDismiss}>
                OK, weiter ohne Vorschlag
              </Button>
            </div>
          </div>
        </Alert>
      </div>
    );
  }

  function setRow(idx: number, patch: Partial<RowState>) {
    setRows((prev) => {
      const next = prev.slice();
      const cur = next[idx];
      if (cur) {
        next[idx] = { ...cur, ...patch };
      }
      return next;
    });
  }

  function setAnswer(idx: number, field: string, value: string) {
    setRows((prev) => {
      const next = prev.slice();
      const cur = next[idx];
      if (cur) {
        next[idx] = {
          ...cur,
          answers: { ...cur.answers, [field]: value },
        };
      }
      return next;
    });
  }

  function buildPositions(): MaPositionInput[] {
    const out: MaPositionInput[] = [];
    rows.forEach((row, idx) => {
      if (!row.selected) return;
      const missing = positions[idx]?.missing ?? [];
      const finalText = composePositionText({
        base: row.text,
        missing,
        answers: row.answers,
      });
      const menge = parseFloat(row.menge.replace(",", "."));
      out.push({
        sort_index: idx,
        menge: Number.isFinite(menge) ? menge : null,
        einheit: row.einheit.trim() || null,
        text: finalText,
        link_url: null,
        notes: row.notes.trim() || null,
      });
    });
    return out;
  }

  const selectedCount = rows.filter((r) => r.selected).length;
  const hasOpenQuestions = rows.some((r, idx) => {
    if (!r.selected) return false;
    const missing = positions[idx]?.missing ?? [];
    return missing.some(
      (m) => !(r.answers[m.field] || "").trim(),
    );
  });

  return (
    <div className="space-y-3 rounded-ios border border-separator bg-card-elevated p-4 shadow-ios">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={clsx(
                "rounded-full px-2.5 py-0.5 text-xs font-semibold",
                KIND_BADGE[result.kind],
              )}
            >
              {kindLabel}
            </span>
            <span className="text-xs text-label-tertiary">
              Sicherheit ca. {confidencePct} % · Modell {result.model}
              {fileName ? ` · Datei „${fileName}“` : ""}
            </span>
          </div>
          {result.global_summary ? (
            <p className="mt-1 text-sm text-label-secondary">
              {result.global_summary}
            </p>
          ) : null}
        </div>
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          Verwerfen
        </Button>
      </header>

      <AuftragHintBadge
        hint={result.auftrag_hint}
        status={auftragHintStatus}
      />

      {result.warnings.length > 0 ? (
        <Alert variant="warning" title="Hinweise der KI">
          <ul className="list-disc pl-5">
            {result.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      <ul className="space-y-3">
        {positions.map((p, idx) => {
          const row = rows[idx];
          if (!row) return null;
          const missing = p.missing ?? [];
          return (
            <li
              key={idx}
              className={clsx(
                "rounded-ios border p-3 transition",
                row.selected
                  ? "border-accent/40 bg-card"
                  : "border-separator bg-grouped",
              )}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <label className="flex flex-1 items-center gap-2 text-sm font-medium text-label">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-separator-opaque text-accent focus:ring-accent-soft"
                    checked={row.selected}
                    onChange={(e) =>
                      setRow(idx, { selected: e.target.checked })
                    }
                  />
                  <span>
                    Pos. {idx + 1}{" "}
                    <span className="ml-1 rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] font-normal text-label-secondary">
                      {CATEGORY_HINT[p.category] || p.category}
                    </span>
                  </span>
                </label>
              </div>

              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[80px_120px_1fr]">
                <input
                  type="text"
                  inputMode="decimal"
                  className="eh-input"
                  placeholder="Menge"
                  value={row.menge}
                  disabled={!row.selected}
                  onChange={(e) => setRow(idx, { menge: e.target.value })}
                />
                <MaSuggestionEinheitField
                  value={row.einheit}
                  disabled={!row.selected}
                  onChange={(einheit) => setRow(idx, { einheit })}
                />
                <input
                  type="text"
                  className="eh-input"
                  placeholder="Bezeichnung"
                  value={row.text}
                  disabled={!row.selected}
                  onChange={(e) => setRow(idx, { text: e.target.value })}
                />
              </div>

              {row.notes || row.selected ? (
                <textarea
                  className="eh-input mt-2"
                  rows={2}
                  placeholder="Optionale Notiz zur Position"
                  value={row.notes}
                  disabled={!row.selected}
                  onChange={(e) => setRow(idx, { notes: e.target.value })}
                />
              ) : null}

              {missing.length > 0 && row.selected ? (
                <MissingFields
                  missing={missing}
                  answers={row.answers}
                  onAnswer={(field, value) => setAnswer(idx, field, value)}
                />
              ) : null}
            </li>
          );
        })}
      </ul>

      <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-separator pt-3">
        {hasOpenQuestions ? (
          <span className="mr-auto text-xs text-status-orange">
            Hinweis: Noch offene Pflichtangaben – diese werden in der
            Position-Beschreibung als „(unklar)“ vermerkt.
          </span>
        ) : null}
        <Button variant="secondary" onClick={onDismiss}>
          Verwerfen
        </Button>
        <Button
          variant="primary"
          disabled={selectedCount === 0}
          onClick={() => onApply(buildPositions())}
        >
          {selectedCount > 0
            ? `${selectedCount} Vorschlag${selectedCount === 1 ? "" : "e"} übernehmen`
            : "Übernehmen"}
        </Button>
      </footer>
    </div>
  );
}

interface MissingFieldsProps {
  missing: AiMissingField[];
  answers: Record<string, string>;
  onAnswer: (field: string, value: string) => void;
}

function MissingFields({ missing, answers, onAnswer }: MissingFieldsProps) {
  return (
    <div className="mt-3 space-y-2 rounded-ios bg-fill-tertiary/60 p-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-status-orange">
        Bitte ergänzen:
      </div>
      <ul className="space-y-2">
        {missing.map((m) => {
          const value = answers[m.field] ?? "";
          return (
            <li key={m.field} className="space-y-1">
              <label className="text-xs font-medium text-label">
                {m.label || m.field}
              </label>
              <p className="text-xs text-label-secondary">{m.question}</p>
              <input
                type="text"
                className="eh-input"
                placeholder="Antwort"
                value={value}
                onChange={(e) => onAnswer(m.field, e.target.value)}
              />
              {m.examples.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {m.examples.map((ex) => (
                    <button
                      type="button"
                      key={ex}
                      className="rounded-full border border-separator bg-card px-2 py-0.5 text-[11px] text-label-secondary hover:bg-fill-tertiary"
                      onClick={() => onAnswer(m.field, ex)}
                    >
                      {ex}
                    </button>
                  ))}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function composePositionText({
  base,
  missing,
  answers,
}: {
  base: string;
  missing: AiMissingField[];
  answers: Record<string, string>;
}): string {
  if (!missing.length) return base.trim();
  const parts: string[] = [];
  for (const m of missing) {
    const a = (answers[m.field] || "").trim();
    if (a) {
      parts.push(`${m.label || m.field}: ${a}`);
    } else {
      parts.push(`${m.label || m.field}: (unklar)`);
    }
  }
  return [base.trim(), parts.join(", ")].filter(Boolean).join(" · ");
}

interface AuftragHintBadgeProps {
  hint: AiAnalysisResult["auftrag_hint"];
  status: AuftragHintStatus | undefined;
}

function AuftragHintBadge({ hint, status }: AuftragHintBadgeProps) {
  if (!hint) return null;

  const baseClass =
    "rounded-ios border px-3 py-2 text-sm flex flex-wrap items-center gap-2";

  const value = (status?.kind && "value" in status ? status.value : hint.value) || hint.value;

  if (!status || status.kind === "idle") {
    return (
      <div className={clsx(baseClass, "border-separator bg-card text-label-secondary")}>
        <span className="font-mono text-xs font-semibold text-label">{value}</span>
        <span>als Auftragsnummer auf dem Bild erkannt.</span>
      </div>
    );
  }

  if (status.kind === "looking") {
    return (
      <div className={clsx(baseClass, "border-separator bg-card text-label-secondary")}>
        <span className="font-mono text-xs font-semibold text-label">{value}</span>
        <span>wird im Hintergrund nachgeschlagen …</span>
      </div>
    );
  }

  if (status.kind === "linked") {
    const a = status.auftrag;
    const subtitle = [a.kunde, a.ort_zusatz || a.ort, a.beschreibung]
      .filter((v): v is string => !!v)
      .join(" · ");
    return (
      <div
        className={clsx(
          baseClass,
          "border-status-green/40 bg-[color-mix(in_srgb,_var(--eh-green)_15%,_var(--eh-bg-card))] text-label",
        )}
      >
        <span className="rounded-full bg-status-green/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-status-green">
          Auftrag verknüpft
        </span>
        <span className="font-mono text-xs font-semibold">{a.auftragsnummer}</span>
        {subtitle ? (
          <span className="text-xs text-label-secondary">· {subtitle}</span>
        ) : null}
      </div>
    );
  }

  if (status.kind === "already-set") {
    return (
      <div
        className={clsx(
          baseClass,
          "border-status-orange/40 bg-[color-mix(in_srgb,_var(--eh-orange)_15%,_var(--eh-bg-card))] text-label",
        )}
      >
        <span className="font-mono text-xs font-semibold text-label">{value}</span>
        <span>
          erkannt – aber „{status.current}“ ist schon zugeordnet. Bitte prüfen
          und ggf. den Auftrag manuell wechseln.
        </span>
      </div>
    );
  }

  if (status.kind === "not-found") {
    return (
      <div
        className={clsx(
          baseClass,
          "border-status-orange/40 bg-[color-mix(in_srgb,_var(--eh-orange)_15%,_var(--eh-bg-card))] text-label",
        )}
      >
        <span className="font-mono text-xs font-semibold">{value}</span>
        <span>
          wurde im Bild erkannt, aber kein passender Auftrag in der App
          gefunden. Bitte oben manuell suchen oder leer lassen.
        </span>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        baseClass,
        "border-status-red/40 bg-[color-mix(in_srgb,_var(--eh-red)_15%,_var(--eh-bg-card))] text-label",
      )}
    >
      <span className="font-mono text-xs font-semibold">{value}</span>
      <span>konnte nicht aufgelöst werden: {status.message}</span>
    </div>
  );
}

function MaSuggestionEinheitField({
  value,
  disabled,
  onChange,
}: {
  value: string;
  disabled?: boolean;
  onChange: (einheit: string) => void;
}) {
  const unitValue = useMemo((): AutocompleteOption<string> | null => {
    const normalized = normalizeMaterialUnit(value);
    if (!normalized) return null;
    return { key: normalized, label: normalized, value: normalized };
  }, [value]);

  return (
    <div className="[&_.eh-input]:text-sm">
      <LockedAutocomplete<string>
        placeholder="Einheit"
        value={unitValue}
        disabled={disabled}
        minChars={0}
        loadOptions={async (q) =>
          filterMaterialUnits(q).map((u) => ({ key: u, label: u, value: u }))
        }
        onChange={(opt) => onChange(opt?.value ?? "")}
      />
    </div>
  );
}
